/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Typography, Typography2, useTheme } from '@coursera/cds-core';
// types
import type { Theme } from '@coursera/cds-core';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import type { FirstNameQuery as FirstNameQueryType } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/__generated__/FirstNameQuery';
import type {
  ProgramDraftQuery as ProgramDraftQueryData,
  ProgramDraftQueryVariables,
} from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/__generated__/ProgramDraftQuery';
import { org_coursera_program_draft_ProgramDraftStatus as ProgramDraftStatus } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/__generated__/globalTypes';
import { FirstNameQuery, ProgramDraftQuery } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/queries';
import GoogleLogoSvg from 'bundles/enterprise-admin-grow-with-google/components/svgs/GoogleLogoSVG';
import {
  GWG_CONTINUE_PROGRAM_SETUP_PATH,
  GWG_PROGRAM_SETUP_PATH,
} from 'bundles/enterprise-admin-grow-with-google/components/withGrowWithGoogleSetup';
import { PersonallyIdentifiableInfo } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-grow-with-google';

type PropsFomCaller = {
  orgSlug: string;
  numOfPrograms: number;
  programSlug: string;
  programId: string;
  isGwGFreemiumMerged?: boolean;
};

type PropsFromFirstNameQuery = {
  firstName?: string;
};

type PropsFromProgramInvitationsQuery = {
  hasLearners?: boolean;
};

export type Props = PropsFomCaller & PropsFromFirstNameQuery & PropsFromProgramInvitationsQuery;

const wrapperStyle = css({
  backgroundColor: '#E9ECF1',
});

const containerStyle = css({
  margin: '0 auto',
  maxWidth: '1170px',
  padding: '56px 1.5rem',
});

const titleStyle = (theme: Theme) =>
  css({
    marginBottom: 'var(--cds-spacing-300)',
    '&&': {
      fontSize: '34px',
      lineHeight: '42px',
    },
  });

const svgContainer = css({
  flexGrow: 1,
});

const descriptionContainer = (theme: Theme) =>
  css({
    marginBottom: 'var(--cds-spacing-300)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  });

const descriptionText = css({
  flexGrow: 3,
});

const descriptionStyle = css({
  '&&': {
    fontSize: '18px',
    lineHeight: '28px',
    maxWidth: '682px',
  },
});

const buttonWrapper = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexGrow: 2,
  'a:first-child': {
    fontWeight: 'bold',
    marginRight: '42px',
    fontSize: '14px',
  },

  'div a': {
    textDecoration: 'underline',
    marginLeft: '8px',
    fontSize: '16px',
    color: '#1859AA',
  },

  div: {
    display: 'flex',
    alignItems: 'center',
  },
});

export const GoogleContinueProgramSetupBanner: React.FC<Props> = ({
  firstName,
  programSlug,
  orgSlug,
  numOfPrograms,
  hasLearners,
  isGwGFreemiumMerged,
}) => {
  const theme = useTheme();
  let shouldRenderContinueProgramSetupBanner: boolean;

  if (isGwGFreemiumMerged) {
    shouldRenderContinueProgramSetupBanner = true;
  } else {
    shouldRenderContinueProgramSetupBanner = numOfPrograms === 1 && !hasLearners;
  }

  const onContinueProgramSetupClick = () =>
    redirect.setLocation(`/o/${orgSlug}/admin/programs/${programSlug}/${GWG_CONTINUE_PROGRAM_SETUP_PATH}`);

  const onAddNewProgramClick = () => redirect.setLocation(`/o/${orgSlug}/admin/programs/${GWG_PROGRAM_SETUP_PATH}`);

  if (shouldRenderContinueProgramSetupBanner) {
    return (
      <div css={wrapperStyle} data-e2e="google-continue-program-setup-banner">
        <div css={containerStyle}>
          <Typography css={titleStyle(theme)} variant="h1semibold">
            {firstName ? (
              <PersonallyIdentifiableInfo>
                <FormattedMessage message={_t('Welcome, {name}!')} name={firstName} />
              </PersonallyIdentifiableInfo>
            ) : (
              <FormattedMessage message={_t('Welcome!')} data-e2e="no-name-welcome" />
            )}
          </Typography>
          <div css={descriptionContainer(theme)}>
            <div css={svgContainer}>
              <GoogleLogoSvg width={48} height={48} />
            </div>
            <div css={descriptionText}>
              <Typography2 component="p" css={descriptionStyle}>
                <span className="bold">{_t('Prepare learners for entry-level digital jobs. ')}</span>
                <span>
                  {_t(
                    ' Give learners access to Google’s Professional Certificates in fields such as IT Support, Data Analytics, Project Management, UX Design, Digital Marketing & E-Commerce, and IT Automation with Python.'
                  )}
                </span>
              </Typography2>
            </div>
            <div css={buttonWrapper}>
              <TrackedCdsButton
                variant="primary"
                onClick={onContinueProgramSetupClick}
                data-e2e="gwg-add-new-program-button"
                trackingName="gwg_continue_program_setup_button"
              >
                {_t('Continue program set up')}
              </TrackedCdsButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div css={wrapperStyle} data-e2e="google-continue-program-setup-banner">
      <div css={containerStyle}>
        <Typography css={titleStyle(theme)} variant="h1semibold">
          {firstName ? (
            <PersonallyIdentifiableInfo>
              <FormattedMessage message={_t('Welcome, {name}!')} name={firstName} />
            </PersonallyIdentifiableInfo>
          ) : (
            <FormattedMessage message={_t('Welcome!')} data-e2e="no-name-welcome" />
          )}
        </Typography>
        <div css={descriptionContainer(theme)}>
          <div css={svgContainer}>
            <GoogleLogoSvg width={48} height={48} />
          </div>
          <div css={descriptionText}>
            <Typography2 component="p" css={descriptionStyle}>
              <span className="bold">{_t('Prepare learners for entry-level digital jobs. ')}</span>
              <span>
                {_t(
                  ' Create as many learning programs as you need. Enable different groups of learners to earn Google Professional Certificates.'
                )}
              </span>
            </Typography2>
          </div>
          <div css={buttonWrapper}>
            <TrackedCdsButton variant="primary" onClick={onAddNewProgramClick} trackingName="gwg_add_program_button">
              {_t('Add new program')}
            </TrackedCdsButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default compose<Props, PropsFomCaller>(
  waitForGraphQL<PropsFomCaller, FirstNameQueryType, {}, PropsFromFirstNameQuery>(FirstNameQuery, {
    props: ({ data }) => {
      return {
        firstName: data?.IdVerificationsV1Resource?.me?.elements[0]?.firstName ?? user.get().fullName?.split(/\s/)[0],
      };
    },
  }),
  graphql<Props, ProgramDraftQueryData, ProgramDraftQueryVariables, PropsFromProgramInvitationsQuery>(
    ProgramDraftQuery,
    {
      // @ts-expect-error react-apollo@2.2.0
      skip: ({ programId, isGwGFreemiumMerged }) => !programId || isGwGFreemiumMerged,
      options: ({ programId }) => ({
        variables: { programId },
      }),
      props: ({ data }) => {
        const status = data?.EnterpriseProgramDraftsV1Resource?.get?.status;
        const hasLearners =
          status === ProgramDraftStatus.CONTENT_AND_MEMBERS || status === ProgramDraftStatus.MEMBERS_ONLY;
        return { hasLearners };
      },
    }
  )
)(GoogleContinueProgramSetupBanner);
